import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Router} from "@angular/router";
import {SpinnerLease, SpinnerService} from "../services/spinner/spinner.service";
import {WebService} from "../services/web/web.service";
import {ToastService} from "../services/notification/toast.service";
import {AuthService} from "../services/auth/auth.service";
import {Location} from "@angular/common";
import {PinAuthContext, PinResendSessionError, SecurityService} from "../services/security.service";
import {LoggerService} from "../services/logger.service";
import {PinValidationService} from "../services/pin-validation.service";
import {InactivityMonitorService} from "../services/inactivity-monitor.service";
import {WebAuthnService} from "../services/webauthn.service";
import {PasskeyService} from "../services/passkey.service";
import {PasskeyErrorHandlerService} from '../services/passkey-error-handler.service';
import {PasskeyAvailabilityState} from '../models/passkey.model';
import {environment} from "../../environments/environment";
import {TranslateService} from "@ngx-translate/core";
import {SessionStoreService} from "../services/session-store.service";
import {Portal} from "../services/portals/portal.model";
import {PortalsService} from "../services/portals/portals.service";
import {finalize} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {PinValidationResponse} from '../models/pin-validation.model';

@Component({
    selector: 'app-validate-pin',
    templateUrl: './validate-pin.component.html',
    styleUrls: ['./validate-pin.component.scss'],
    standalone : false
})
export class ValidatePinComponent implements OnInit, OnDestroy {
    validateForm: FormGroup;
    
    // 👇 añadidos para el flujo admin
    modoSeleccionPortal: boolean = false;
    portals: Portal[] = [];
    
    // Control para mostrar/ocultar el PIN
    showPin = false;
    
    // Mensaje de ayuda con intentos restantes
    helpMessage = '';

    // ═══════════════════════════════════════════════════════════════════
    // PROPIEDADES PARA PASSKEY
    // ═══════════════════════════════════════════════════════════════════
    
    /**
     * Indica si se debe mostrar la opción de validación con Passkey.
     * Se activa cuando:
     * 1. El navegador soporta WebAuthn
     * 2. El usuario tiene al menos una Passkey registrada
     */
    showPasskeyOption: boolean = false;

    /**
     * Indica si se está procesando una validación con Passkey.
     * Se usa para mostrar spinner y deshabilitar botones.
     */
    isLoadingPasskey: boolean = false;

    /**
     * Almacena el mensaje de error si falla la validación con Passkey.
     * Se muestra al usuario para que sepa qué salió mal.
     */
    passkeyError: string | null = null;

    /**
     * Nombre del método biométrico disponible en el dispositivo.
     * Ejemplos: "Face ID", "Touch ID", "Windows Hello", "Huella Digital"
     * Se usa para personalizar el texto del botón.
     */
    biometricMethodName: string = 'Passkey';

    /**
     * Indica si el navegador soporta WebAuthn.
     * Se verifica una sola vez en ngOnInit.
     */
    webAuthnSupported: boolean = false;

    /**
     * Controla la visibilidad del modal de onboarding de passkey.
     * Se muestra después de validar PIN exitosamente si el usuario NO tiene passkey.
     */
    showOnboardingModal: boolean = false;

    /**
     * Indica si se está verificando la disponibilidad de passkey.
     * Se usa para mostrar un estado de carga inicial.
     */
    isCheckingPasskey: boolean = true;

    /**
     * Indica qué método de validación está activo.
     * 'passkey' = Muestra solo la opción de passkey
     * 'pin' = Muestra solo el formulario de PIN
     */
    activeMethod: 'passkey' | 'pin' = 'passkey';
    passkeyAvailabilityState: PasskeyAvailabilityState = 'idle';
    pinExplicitlySelected = false;

    /**
     * Indica si se debe ocultar el formulario de validación.
     * Se activa cuando el usuario viene de login con passkey.
     */
    hideValidationForm: boolean = false;
    isSubmittingPin = false;
    isResendingPin = false;
    private pinSubscription: Subscription | null = null;
    private pinLease: SpinnerLease | null = null;
    private authContextSubscription: Subscription | null = null;
    private pinGeneration = 0;
    private pinFailureTimer: ReturnType<typeof setTimeout> | null = null;
    private passkeyAbortController: AbortController | null = null;
    private passkeyGeneration = 0;
    private passkeyLookupAbortController: AbortController | null = null;
    private passkeyLookupGeneration = 0;
    private continueToAppTimer: ReturnType<typeof setTimeout> | null = null;
    private portalsSubscription: Subscription | null = null;
    private portalsGeneration = 0;
    private onboardingContext: PinAuthContext | null = null;
    private readonly passkeyOperationTimeoutMs = 90000;

    constructor(
        private securityService: SecurityService,
        private router: Router,
        private spinner: SpinnerService,
        private webService: WebService,
        private toast: ToastService,
        private auth: AuthService,
        private location: Location,
        private logger: LoggerService,
        private pinValidationService: PinValidationService,
        private inactivityMonitor: InactivityMonitorService,
        private webAuthnService: WebAuthnService,
        private passkeyService: PasskeyService,
        private passkeyErrorHandler: PasskeyErrorHandlerService,
        private translate: TranslateService,
        private sessionStore: SessionStoreService,
        private portalsService: PortalsService
    ) {
        this.validateForm = new FormGroup({
            pin: new FormControl('', [Validators.required]),
            email: new FormControl(''),
            partner: new FormControl('')
        });
    }

    async ngOnInit(): Promise<void> {
        const authContextChanges$ = (this.securityService as any).authContextChanges$;
        if (authContextChanges$) {
            this.authContextSubscription = authContextChanges$.subscribe(() => this.cancelAuthContextOperations());
        }
        this.logger.log('🔵 [VALIDATE-PIN] Componente de validación de PIN inicializado');
        this.pinValidationService.resetPinSentFlag();
        
        // �� NUEVO: Verificar si viene de autenticación con passkey
        const pendingMfaTokenRestored = this.securityService.restorePendingMfaToken();
        if (pendingMfaTokenRestored) {
            this.logger.log('[VALIDATE-PIN] Token MFA pendiente restaurado');
        }

        const authenticatedWithPasskey = sessionStorage.getItem('authenticated_with_passkey');
        
        if (authenticatedWithPasskey === 'true') {
            this.logger.log('🔐 [VALIDATE-PIN] Usuario autenticado con passkey - Ejecutando lógica sin mostrar formularios');
            
            // Limpiar el flag
            sessionStorage.removeItem('authenticated_with_passkey');
            
            // Ocultar formularios
            this.isCheckingPasskey = false;
            this.showPasskeyOption = false;
            this.hideValidationForm = true; // ✅ Ocultar formulario
            
            // Pequeño delay para asegurar que el token esté disponible
            const context = this.capturePinContext();
            this.clearContinueToAppTimer();
            this.continueToAppTimer = setTimeout(() => {
                this.continueToAppTimer = null;
                if (!this.isSamePinAuthContext(context, this.capturePinContext())) return;
                // Ejecutar lógica de continuación (verificar admin, etc.)
                this.continueToApp();
            }, 100);
            return;
        }

        // Una pestaña nueva conserva la cookie/MFA, pero no el portal elegido.
        // Reconstruir la selección admin desde el JWT restaurado.
        if (this.securityService.getPinValidationStatus() &&
            this.securityService.isSuperAdmin() &&
            !this.sessionStore.getSesion()) {
            this.continueToApp();
            return;
        }
        
        // Flujo normal: Verificar si tiene passkey
        await this.checkPasskeyAvailability();
        
        // IMPORTANTE: NO enviar PIN automáticamente
        // El backend ya envió el PIN durante el login tradicional (user+password o Google)
        // Solo se debe enviar PIN cuando:
        // 1. El usuario TIENE passkey pero elige manualmente "Usar PIN por correo"
        // 2. El usuario hace click en "Reenviar PIN"
        
        if (!this.showPasskeyOption) {
            this.logger.log('📧 [VALIDATE-PIN] Usuario NO tiene passkey');
            this.logger.log('ℹ️ [VALIDATE-PIN] El PIN ya fue enviado durante el login - NO se reenvía automáticamente');
        } else {
            this.logger.log('🔐 [VALIDATE-PIN] Usuario tiene passkey - Mostrando opción de validación rápida');
            this.logger.log('ℹ️ [VALIDATE-PIN] PIN solo se enviará si el usuario elige "Usar PIN por correo"');
        }
    }

    ngOnDestroy(): void {
        this.authContextSubscription?.unsubscribe();
        this.cancelPinOperation();
        this.cancelPasskeyOperation();
        this.cancelPasskeyLookup();
        this.cancelPortalsOperation();
        this.invalidateOnboardingModal();
        this.clearContinueToAppTimer();
    }

    validate() {
        if (this.isSubmittingPin || this.isResendingPin || this.pinSubscription || this.validateForm.invalid) return;
        const pinValue = this.validateForm.get('pin')?.value;
        const generation = this.startPinOperation();
        const context = this.capturePinContext();
        this.isSubmittingPin = true;
        const lease = this.spinner.acquire();
        this.pinLease = lease;
        const request = this.securityService.validatePinForCurrentSession(pinValue).pipe(
            finalize(() => {
                lease.release();
                if (!this.isCurrentPinOperation(generation, context)) return;
                this.isSubmittingPin = false;
                if (this.pinLease === lease) this.pinLease = null;
                this.pinSubscription = null;
            })
        ).subscribe((res: PinValidationResponse) => {
            if (!this.isCurrentPinOperation(generation, context)) return;
            if (res.valid !== true) {
                if (this.isSessionInvalid(res)) {
                    this.restartAuthentication();
                    return;
                }
                this.showPinValidationFailure(res, generation, context);
                this.securityService.isPinValidated(false);
                return;
            }
            
            // Marcar PIN como validado
            this.securityService.isPinValidated(true);
            this.securityService.clearPendingMfaToken();
            
            // Reiniciar actividad
            this.inactivityMonitor.markPinValidated();
            
            // NUEVO: Verificar si debe mostrar onboarding de passkey
            this.checkAndShowPasskeyOnboarding();

        }, (err: unknown) => {
            if (!this.isCurrentPinOperation(generation, context)) return;
            if (this.isSessionInvalid(err)) {
                this.restartAuthentication();
                return;
            }
            this.showPinValidationFailure(err, generation, context);
            this.securityService.isPinValidated(false);
        });
        this.pinSubscription = request;
        if (request.closed) this.pinSubscription = null;
    }

    /**
     * Regresa al login cerrando completamente la sesión
     * Limpia TODOS los datos de sesión, tokens y estados
     */
    goLogin() {
        this.cancelPinOperation();
        this.cancelPasskeyOperation();
        this.logger.log('🔙 [VALIDATE-PIN] Usuario hizo click en "Volver al inicio de sesión"');
        this.logger.log('🧹 [VALIDATE-PIN] Limpiando estado y cerrando sesión...');
        
        this.securityService.isPinValidated(false);
        this.securityService.clearPendingMfaToken();
        this.pinValidationService.resetFailedAttempts();
        
        this.validateForm.reset();
        this.helpMessage = '';
        this.showPin = false;
        
        // Marcar que el usuario hizo logout para evitar auto-login con Google
        this.logger.log('🚫 [VALIDATE-PIN] Marcando flag para evitar auto-login con Google');
        sessionStorage.setItem('just_logged_out', 'true');
        
        this.logger.log('🚪 [VALIDATE-PIN] Ejecutando logout...');
        this.securityService.logout();
    }

    resendPin() {
        if (this.isResendingPin || this.isSubmittingPin || this.pinSubscription) return;
        this.logger.log('📧 [VALIDATE-PIN] Usuario solicitó reenvío de PIN');
        const generation = this.startPinOperation();
        const context = this.capturePinContext();
        this.isResendingPin = true;
        const lease = this.spinner.acquire();
        this.pinLease = lease;
        const request = this.securityService.resendPinForCurrentSession().pipe(finalize(() => {
            lease.release();
            if (!this.isCurrentPinOperation(generation, context)) return;
            this.isResendingPin = false;
            if (this.pinLease === lease) this.pinLease = null;
            this.pinSubscription = null;
        })).subscribe(() => {
            if (!this.isCurrentPinOperation(generation, context)) return;
            this.logger.log('✅ [VALIDATE-PIN] PIN reenviado exitosamente');
            this.pinValidationService.markPinAsSent();
            this.toast.showSuccess(this.translate.instant('validatePin.messages.pinSent'));
        }, (error: unknown) => {
            if (!this.isCurrentPinOperation(generation, context)) return;
            this.logger.error('❌ [VALIDATE-PIN] Error al reenviar PIN');
            this.pinValidationService.resetPinSentFlag();
            if (this.isSessionInvalid(error)) {
                this.restartAuthentication();
                return;
            }
            this.toast.showError(this.translate.instant('validatePin.messages.pinError'));
        });
        this.pinSubscription = request;
        if (request.closed) this.pinSubscription = null;
    }

    /**
     * Envía PIN automáticamente al cargar el componente.
     * 
     * Solo se ejecuta si el usuario NO tiene passkey registrada.
     * Si tiene passkey, el PIN solo se envía si el usuario lo solicita manualmente.
     */
    private sendPinAutomatically(): void {
        if (this.isResendingPin || this.isSubmittingPin || this.pinSubscription) return;
        const generation = this.startPinOperation();
        const context = this.capturePinContext();
        this.isResendingPin = true;
        const request = this.securityService.resendPinForCurrentSession().pipe(finalize(() => {
            if (!this.isCurrentPinOperation(generation, context)) return;
            this.isResendingPin = false;
            this.pinSubscription = null;
        })).subscribe(
            res => {
                if (!this.isCurrentPinOperation(generation, context)) return;
                this.logger.log('✅ [VALIDATE-PIN] PIN enviado exitosamente');
                this.pinValidationService.markPinAsSent();
                this.toast.showInfo(this.translate.instant('validatePin.messages.pinSentInfo'));
            },
            (error: unknown) => {
                if (!this.isCurrentPinOperation(generation, context)) return;
                this.logger.error('❌ [VALIDATE-PIN] Error al enviar PIN');
                this.pinValidationService.resetPinSentFlag();
            if (this.isSessionInvalid(error)) this.restartAuthentication();
            }
        );
        this.pinSubscription = request;
        if (request.closed) this.pinSubscription = null;
    }

    /**
     * Alterna la visibilidad del PIN
     * Patrón uniforme con PreferencesComponent
     */
    toggleShowPin(): void {
        this.showPin = !this.showPin;
    }

    /**
     * Cambia al método de validación por PIN.
     * Oculta la opción de passkey y muestra el formulario de PIN.
     * Envía el PIN automáticamente al correo del usuario.
     */
    switchToPinMethod(): void {
        this.cancelPinOperation();
        this.cancelPasskeyOperation();
        this.cancelPasskeyLookup();
        this.logger.log('🔄 [VALIDATE-PIN] ========================================');
        this.logger.log('🔄 [VALIDATE-PIN] Usuario eligió validar con PIN');
        this.logger.log('🔄 [VALIDATE-PIN] Cambiando activeMethod de "passkey" a "pin"');
        this.activeMethod = 'pin';
        this.pinExplicitlySelected = true;
        
        // Enviar PIN automáticamente
        this.logger.log('📧 [VALIDATE-PIN] Enviando PIN por solicitud del usuario...');
        this.sendPinAutomatically();
    }

    /**
     * Cambia al método de validación por Passkey.
     * Oculta el formulario de PIN y muestra la opción de passkey.
     */
    switchToPasskeyMethod(): void {
        this.cancelPinOperation();
        this.cancelPasskeyLookup();
        this.logger.log('🔄 [VALIDATE-PIN] Usuario eligió validar con Passkey');
        this.activeMethod = 'passkey';
        
        // Limpiar errores previos
        this.passkeyError = null;
        this.helpMessage = '';
    }

    async retryPasskeyLookup(): Promise<void> {
        if (this.isCheckingPasskey) return;
        this.passkeyError = null;
        await this.checkPasskeyAvailability();
    }

    // ═══════════════════════════════════════════════════════════════════
    // MÉTODOS PARA PASSKEY
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Ejecuta la validación con Passkey (re-validación).
     * 
     * Este método reemplaza la validación de PIN por email con una
     * validación biométrica instantánea.
     * 
     * Flujo:
     * 1. Llama al PasskeyService para re-validar
     * 2. Marca PIN como validado
     * 3. Reinicia el registro de actividad
     * 4. Redirige según tipo de usuario (admin o regular)
     * 
     * Ventajas sobre PIN:
     * - No requiere acceso al correo
     * - Más rápido (1-2 segundos vs minutos)
     * - Más seguro (biométrico)
     * - Mejor UX
     */
    async validateWithPasskey(): Promise<void> {
        this.logger.log('🔐 [VALIDATE-PIN-PASSKEY] ========================================');
        this.logger.log('🔐 [VALIDATE-PIN-PASSKEY] Iniciando validación con Passkey');
        
        if (this.isLoadingPasskey) return;
        this.cancelPasskeyLookup();
        const operation = this.startPasskeyOperation();
        const generation = this.passkeyGeneration;
        const context = this.capturePinContext();
        this.isLoadingPasskey = true;
        this.passkeyError = null;

        try {
            this.logger.log('📡 [VALIDATE-PIN-PASSKEY] Llamando a PasskeyService.revalidateWithPasskey()...');
            
            // Ejecutar re-validación con passkey
            const success = await this.passkeyService.revalidateWithPasskey(operation);
            if (!this.isCurrentPasskeyOperation(generation, context)) return;

            if (!success) {
                throw new Error('Re-validación fallida');
            }

            this.logger.log('✅ [VALIDATE-PIN-PASSKEY] Re-validación exitosa');

            // Marcar PIN como validado
            this.logger.log('✅ [VALIDATE-PIN-PASSKEY] Marcando PIN como validado');
            this.securityService.isPinValidated(true);
            if (!this.isCurrentPasskeyOperation(generation, context)) return;
            
            // Reiniciar actividad
            this.logger.log('🔄 [VALIDATE-PIN-PASSKEY] Reiniciando actividad');
            this.inactivityMonitor.markPinValidated();
            if (!this.isCurrentPasskeyOperation(generation, context)) return;
            
            // Resetear intentos fallidos
            this.pinValidationService.resetFailedAttempts();

            // Obtener información del token
            const decodedToken = this.securityService.getDecodedToken();
            
            if (!decodedToken || !decodedToken.payload) {
                throw new Error("Error al procesar la sesión");
            }
            
            const payload = decodedToken.payload;
            if (this.handleAdminPortalSelection('VALIDATE-PIN-PASSKEY')) return;

            // Caso NO ADMIN
            this.logger.log('👤 [VALIDATE-PIN-PASSKEY] Usuario regular - Redirigiendo a /my-portals');
            this.toast.showSuccess(this.translate.instant('validatePin.messages.identityVerified', { method: this.biometricMethodName }));
            
            this.sessionStore.setSesion(payload);

            const config = payload.config;
            if (Array.isArray(config)) {
                this.router.navigateByUrl('/my-portals');
                return;
            }

            this.router.navigate(['/my-portals']);

        } catch (error: unknown) {
            if (!this.isCurrentPasskeyOperation(generation, context)) return;
            this.logger.error('❌ [VALIDATE-PIN-PASSKEY] Error en validación con passkey');
            
            const result = this.passkeyErrorHandler.handlePasskeyError(error, 'revalidation');
            this.passkeyError = result.message;
            this.toast.showError(result.message);
        } finally {
            if (this.isCurrentPasskeyOperation(generation, context)) {
                this.isLoadingPasskey = false;
                this.passkeyAbortController = null;
            }
        }
    }

    private showPinValidationFailure(
        response: unknown,
        generation: number,
        context: { email: string | null; partner: number | null; sessionId: string | null; authRevision: number }
    ): void {
        const failure = response as (PinValidationResponse & { error?: PinValidationResponse }) | undefined;
        const code = failure?.code ?? failure?.error?.code;
        if (code !== 'PIN_INVALID_OR_EXPIRED') {
            this.helpMessage = this.translate.instant('validatePin.messages.pinValidationUnavailable');
            this.toast.showError(this.helpMessage);
            return;
        }
        this.pinValidationService.incrementFailedAttempts();
        const failedAttempts = this.pinValidationService.getFailedAttempts();
        const maxAttempts = this.pinValidationService.getMaxAttempts();
        if (this.pinValidationService.hasReachedMaxAttempts()) {
            this.helpMessage = this.translate.instant('validatePin.messages.maxAttemptsReached', { maxAttempts });
            this.clearPinFailureTimer();
            this.pinFailureTimer = setTimeout(() => {
                if (this.isCurrentPinOperation(generation, context)) this.securityService.logout();
            }, 2000);
            return;
        }
        const remainingAttempts = maxAttempts - failedAttempts;
        this.helpMessage = this.translate.instant('validatePin.messages.invalidPin', {
            remaining: remainingAttempts, plural: remainingAttempts > 1 ? 's' : ''
        });
        this.toast.showError(this.helpMessage);
    }

    get showPinForm(): boolean {
        return this.pinExplicitlySelected || this.passkeyAvailabilityState === 'absent' || this.passkeyAvailabilityState === 'idle';
    }

    get pinWasSent(): boolean {
        return this.pinValidationService.wasPinSentInCurrentSession();
    }

    private isSessionInvalid(response: unknown): boolean {
        return response instanceof PinResendSessionError;
    }

    private restartAuthentication(): void {
        this.cancelPinOperation();
        this.cancelPasskeyOperation();
        this.cancelPasskeyLookup();
        this.securityService.isPinValidated(false);
        this.securityService.clearPendingMfaToken();
        this.pinValidationService.resetFailedAttempts();
        this.pinValidationService.resetPinSentFlag();
        this.securityService.logout();
    }

    private cancelPinOperation(): void {
        this.pinGeneration++;
        this.pinSubscription?.unsubscribe();
        this.pinSubscription = null;
        this.isSubmittingPin = false;
        this.isResendingPin = false;
        this.pinLease?.release();
        this.pinLease = null;
        this.clearPinFailureTimer();
    }

    private startPinOperation(): number {
        return ++this.pinGeneration;
    }

    private capturePinContext(): PinAuthContext {
        const authContext = (this.securityService as any).capturePinAuthContext?.();
        if (authContext) return authContext;
        const payload = this.securityService.getDecodedToken()?.payload;
        return {
            email: this.securityService.getTokenSub(),
            partner: null,
            sessionId: payload?.session_id ?? null,
            authRevision: 0
        };
    }

    private isCurrentPinOperation(
        generation: number,
        context: { email: string | null; partner: number | null; sessionId: string | null; authRevision: number }
    ): boolean {
        const current = this.capturePinContext();
        return generation === this.pinGeneration && this.isSamePinAuthContext(context, current);
    }

    private isSamePinAuthContext(left: PinAuthContext, right: PinAuthContext): boolean {
        return left.email === right.email &&
            left.partner === right.partner &&
            left.sessionId === right.sessionId &&
            left.authRevision === right.authRevision;
    }

    private clearPinFailureTimer(): void {
        if (this.pinFailureTimer !== null) {
            clearTimeout(this.pinFailureTimer);
            this.pinFailureTimer = null;
        }
    }

    private clearContinueToAppTimer(): void {
        if (this.continueToAppTimer !== null) {
            clearTimeout(this.continueToAppTimer);
            this.continueToAppTimer = null;
        }
    }

    private cancelAuthContextOperations(): void {
        this.cancelPinOperation();
        this.cancelPasskeyOperation();
        this.cancelPasskeyLookup();
        this.cancelPortalsOperation();
        this.invalidateOnboardingModal();
        this.clearContinueToAppTimer();
    }

    private cancelPortalsOperation(): void {
        this.portalsGeneration++;
        this.portalsSubscription?.unsubscribe();
        this.portalsSubscription = null;
    }

    private isCurrentPortalsOperation(generation: number, context: PinAuthContext): boolean {
        return generation === this.portalsGeneration && this.isSamePinAuthContext(context, this.capturePinContext());
    }

    private openOnboardingModal(context: PinAuthContext): void {
        this.onboardingContext = context;
        this.showOnboardingModal = true;
    }

    private invalidateOnboardingModal(): void {
        this.onboardingContext = null;
        this.showOnboardingModal = false;
    }

    private startPasskeyOperation(): { signal: AbortSignal; deadlineAt: number } {
        this.cancelPasskeyLookup();
        this.cancelPasskeyOperation();
        this.passkeyAbortController = new AbortController();
        this.passkeyGeneration++;
        return { signal: this.passkeyAbortController.signal, deadlineAt: Date.now() + this.passkeyOperationTimeoutMs };
    }

    private cancelPasskeyOperation(): void {
        this.passkeyGeneration++;
        this.passkeyAbortController?.abort();
        this.passkeyAbortController = null;
        this.isLoadingPasskey = false;
    }

    private isCurrentPasskeyOperation(generation: number, context: PinAuthContext): boolean {
        return generation === this.passkeyGeneration &&
            !this.passkeyAbortController?.signal.aborted &&
            this.isSamePinAuthContext(context, this.capturePinContext());
    }

    private startPasskeyLookup(): { signal: AbortSignal; deadlineAt: number } {
        this.cancelPasskeyLookup();
        this.passkeyLookupAbortController = new AbortController();
        this.passkeyLookupGeneration++;
        return {
            signal: this.passkeyLookupAbortController.signal,
            deadlineAt: Date.now() + this.passkeyOperationTimeoutMs
        };
    }

    private cancelPasskeyLookup(): void {
        this.passkeyLookupGeneration++;
        this.passkeyLookupAbortController?.abort();
        this.passkeyLookupAbortController = null;
        this.isCheckingPasskey = false;
    }

    private capturePasskeyLookupContext(email: string): PinAuthContext {
        return { ...this.capturePinContext(), email };
    }

    private isCurrentPasskeyLookup(
        generation: number,
        context: PinAuthContext
    ): boolean {
        const email = this.securityService.getTokenSub();
        if (!email) return false;
        const current = this.capturePasskeyLookupContext(email);
        return generation === this.passkeyLookupGeneration &&
            !this.passkeyLookupAbortController?.signal.aborted &&
            this.isSamePinAuthContext(context, current);
    }

    /**
     * Verifica si el usuario tiene passkey registrada.
     * 
     * Se ejecuta en ngOnInit para determinar si mostrar la opción
     * de validación rápida con passkey.
     */
    private async checkPasskeyAvailability(): Promise<void> {
        this.logger.log('🔍 [VALIDATE-PIN] Verificando disponibilidad de passkey...');
        this.cancelPasskeyLookup();
        this.isCheckingPasskey = true;
        this.passkeyAvailabilityState = 'checking';
        this.showPasskeyOption = false;
        
        // Verificar soporte de WebAuthn
        this.webAuthnSupported = this.webAuthnService.isWebAuthnSupported();
        
        if (!this.webAuthnSupported) {
            this.logger.warn('⚠️ [VALIDATE-PIN] WebAuthn no soportado');
            this.isCheckingPasskey = false;
            this.passkeyAvailabilityState = 'idle';
            return;
        }

        // Obtener email del usuario
        const email = this.securityService.getTokenSub();

        if (!email) {
            this.logger.warn('⚠️ [VALIDATE-PIN] No se pudo obtener email del usuario');
            this.isCheckingPasskey = false;
            this.passkeyAvailabilityState = 'idle';
            return;
        }

        const context = this.capturePasskeyLookupContext(email);
        const operation = this.startPasskeyLookup();
        const generation = this.passkeyLookupGeneration;
        try {
            this.logger.log('📡 [VALIDATE-PIN] Verificando passkey');
            const hasPasskey = await this.passkeyService.hasPasskey(context.email ?? undefined, context.partner ?? undefined, operation);
            if (!this.isCurrentPasskeyLookup(generation, context)) return;
            
            if (hasPasskey) {
                this.passkeyAvailabilityState = 'available';
                this.logger.log('✅ [VALIDATE-PIN] Usuario tiene passkey - Inicializando opción de validación rápida');
                this.showPasskeyOption = true;
                this.biometricMethodName = await this.webAuthnService.getBiometricMethodName();
                if (!this.isCurrentPasskeyLookup(generation, context)) return;
            } else {
                this.passkeyAvailabilityState = 'absent';
                this.logger.log('ℹ️ [VALIDATE-PIN] Usuario NO tiene passkey');
            }
        } catch (error) {
            if (!this.isCurrentPasskeyLookup(generation, context)) return;
            this.logger.error('❌ [VALIDATE-PIN] Error verificando passkey');
            this.passkeyAvailabilityState = 'error';
            this.pinValidationService.resetPinSentFlag();
        } finally {
            if (this.isCurrentPasskeyLookup(generation, context)) {
                this.isCheckingPasskey = false;
                this.passkeyLookupAbortController = null;
                this.logger.log('✅ [VALIDATE-PIN] Verificación de passkey completada');
            }
        }
    }

    /**
     * Verifica si debe mostrar el modal de onboarding de passkey.
     * 
     * Se ejecuta después de validar PIN exitosamente.
     * 
     * Condiciones para mostrar el modal:
     * 1. Navegador soporta WebAuthn
     * 2. Usuario NO ha rechazado el onboarding antes
     * 3. Usuario NO tiene passkey registrada
     */
    private async checkAndShowPasskeyOnboarding(): Promise<void> {
        this.logger.log('🔍 [VALIDATE-PIN] Verificando si mostrar onboarding de passkey...');
        
        // 1. Verificar soporte de WebAuthn
        if (!this.webAuthnService.isWebAuthnSupported()) {
            this.logger.log('⚠️ [VALIDATE-PIN] WebAuthn no soportado - Continuando sin passkey');
            this.continueToApp();
            return;
        }

        // 2. Verificar si ya rechazó el onboarding antes
        const hasDeclinedOnboarding = localStorage.getItem('passkey_onboarding_declined');
        if (hasDeclinedOnboarding === 'true') {
            this.logger.log('ℹ️ [VALIDATE-PIN] Usuario ya rechazó onboarding - Continuando sin passkey');
            this.continueToApp();
            return;
        }

        // 3. Verificar si ya tiene passkey
        const email = this.securityService.getTokenSub();

        if (!email) {
            this.logger.warn('⚠️ [VALIDATE-PIN] No se pudo obtener email - Continuando sin passkey');
            this.continueToApp();
            return;
        }

        const context = this.capturePasskeyLookupContext(email);
        const operation = this.startPasskeyLookup();
        const generation = this.passkeyLookupGeneration;
        try {
            const hasPasskey = await this.passkeyService.hasPasskey(context.email ?? undefined, context.partner ?? undefined, operation);
            if (!this.isCurrentPasskeyLookup(generation, context)) return;
            
            if (hasPasskey) {
                this.passkeyAvailabilityState = 'available';
                // Ya tiene passkey, continuar normalmente
                this.logger.log('✅ [VALIDATE-PIN] Usuario ya tiene passkey - Continuando a la app');
                this.continueToApp();
            } else {
                this.passkeyAvailabilityState = 'absent';
                // NO tiene passkey → Mostrar modal de onboarding
                this.logger.log('🎯 [VALIDATE-PIN] Usuario NO tiene passkey - Mostrando modal de onboarding');
                this.openOnboardingModal(context);
            }
        } catch (error) {
            if (!this.isCurrentPasskeyLookup(generation, context)) return;
            this.logger.error('❌ [VALIDATE-PIN] Error verificando passkey');
            this.passkeyAvailabilityState = 'error';
            this.openOnboardingModal(context);
        } finally {
            if (this.isCurrentPasskeyLookup(generation, context)) {
                this.passkeyLookupAbortController = null;
            }
        }
    }

    /**
     * Continúa a la aplicación después de validar PIN.
     * 
     * Maneja tanto el caso de usuarios admin como regulares.
     */
    private continueToApp(): void {
        this.logger.log('🚀 [VALIDATE-PIN] Continuando a la aplicación...');
        
        const decodedToken = this.securityService.getDecodedToken();
        
        if (!decodedToken || !decodedToken.payload) {
            this.toast.showError(this.translate.instant('validatePin.messages.sessionError'));
            return;
        }
        
        const payload = decodedToken.payload;
        if (this.handleAdminPortalSelection('VALIDATE-PIN')) return;

        // Caso NO ADMIN
        this.logger.log('👤 [VALIDATE-PIN] Usuario regular - Redirigiendo a /my-portals');
        this.toast.showSuccess(this.translate.instant('validatePin.messages.loginSuccess'));
        
        this.sessionStore.setSesion(payload);

        const config = payload.config;
        if (Array.isArray(config)) {
            this.router.navigateByUrl('/my-portals');
            return;
        }

        this.router.navigate(['/my-portals']);
    }

    private handleAdminPortalSelection(logContext: string): boolean {
        if (!this.securityService.isSuperAdmin()) return false;

        this.logger.log(`👤 [${logContext}] Usuario es ADMIN - Cargando portales`);
        this.cancelPortalsOperation();
        const generation = ++this.portalsGeneration;
        const context = this.capturePinContext();
        const request = this.portalsService.getPortals$().subscribe({
            next: (portals) => {
                if (!this.isCurrentPortalsOperation(generation, context)) return;
                this.portals = portals;
                this.modoSeleccionPortal = true;
                this.toast.showInfo(this.translate.instant('validatePin.messages.selectPortal'));
            },
            error: () => {
                if (!this.isCurrentPortalsOperation(generation, context)) return;
                this.sessionStore.clear();
                this.toast.showError(this.translate.instant('validatePin.messages.sessionError'));
            }
        });
        this.portalsSubscription = request.closed ? null : request;
        return true;
    }

    /**
     * Callback cuando se cierra el modal de onboarding.
     * 
     * @param passkeyRegistered - true si se registró la passkey, false si se rechazó
     */
    onOnboardingModalClose(passkeyRegistered: boolean): void {
        const context = this.onboardingContext;
        if (!context || !this.showOnboardingModal || !this.isSamePinAuthContext(context, this.capturePinContext())) return;
        this.logger.log('🔔 [VALIDATE-PIN] Modal de onboarding cerrado');
        this.logger.log('   → Passkey registrada:', passkeyRegistered);
        
        this.invalidateOnboardingModal();
        
        if (passkeyRegistered) {
            this.logger.log('✅ [VALIDATE-PIN] Passkey registrada exitosamente');
            // El toast de éxito ya se muestra en el modal
        } else {
            this.logger.log('ℹ️ [VALIDATE-PIN] Usuario rechazó activar passkey');
        }
        
        // Continuar a la app
        this.continueToApp();
    }

}
